import { ApiOptions, OrgId } from '@csp/csp-common-model';
import {
  findScheduleVersionsActiveNow,
  OrgSchedules,
  OrgSchedulesMapper,
  OrgsSchedules,
  ScheduleCachedService,
} from '@csp/csp-common-scheduling';
import { toRestOptions } from '@csp/csp-fe-auth';
import { AppointmentRestServiceV1 } from '@csp/dmdp-api-client';
import { StudyConfig } from '@csp/csp-common-config';
import { VisitRequest } from '../model/VisitRequest';
import { VisitSchedule } from '../model/VisitSchedule';

const getVisitSchedules = async (orgId: string, apiOptions?: ApiOptions): Promise<VisitSchedule[]> => {
  const { activeSchedules = [] } = await AppointmentRestServiceV1.getActiveSchedulesByOrgId(
    toRestOptions(apiOptions),
    orgId,
  );

  return activeSchedules.map(VisitSchedule.from);
};

const getActiveRequestsByOrgId = async (
  orgId: string,
  studyConfig?: StudyConfig,
  apiOptions?: ApiOptions,
): Promise<VisitRequest[]> => {
  const visitSchedules = await getVisitSchedules(orgId, apiOptions);
  const defaultSchedule = VisitSchedule.toDefaultVisitSchedule(
    studyConfig?.visitConfig.supportedUnscheduledVisitLocationTypes ?? [],
  );
  const allSchedules = [...visitSchedules, defaultSchedule];
  return findScheduleVersionsActiveNow(allSchedules).flatMap(({ requests }) => requests);
};

const getCachedActiveRequestsByOrgId = async (orgId: string, apiOptions?: ApiOptions): Promise<VisitRequest[]> => {
  const orgSchedules = await getCachedActivatedVisitSchedulesForOrganizationIds([orgId], undefined, apiOptions);
  return orgSchedules.getActiveRequests();
};

const getActivatedVisitSchedulesForOrganizationIds = async (
  organizationIds: OrgId[],
  studyConfig?: StudyConfig,
  apiOptions?: ApiOptions,
): Promise<OrgsSchedules<VisitSchedule, VisitRequest>> => {
  const appointmentActiveScheduleOrganizationsV1 = await AppointmentRestServiceV1.queryActiveSchedulesByOrganizationIds(
    toRestOptions(apiOptions),
    { orgIds: organizationIds },
  );
  const defaultSchedule = VisitSchedule.toDefaultVisitSchedule(
    studyConfig?.visitConfig.supportedUnscheduledVisitLocationTypes ?? [],
  );

  const orgsSchedules = OrgsSchedules.from<VisitSchedule, VisitRequest>(
    OrgSchedulesMapper.fromV1s(appointmentActiveScheduleOrganizationsV1, VisitSchedule.from),
  );

  const orgSchedulesWithDefault = orgsSchedules.orgSchedules.map(
    (orgSchedule): OrgSchedules<VisitSchedule> => ({
      schedules: [...orgSchedule.schedules, defaultSchedule],
      orgId: orgSchedule.orgId,
    }),
  );
  return OrgsSchedules.from<VisitSchedule, VisitRequest>(orgSchedulesWithDefault);
};

const getCachedActivatedVisitSchedulesForOrganizationIds = async (
  organizationIds: OrgId[],
  studyConfig?: StudyConfig,
  apiOptions?: ApiOptions,
): Promise<OrgsSchedules<VisitSchedule, VisitRequest>> =>
  ScheduleCachedService.getAllActivatedSchedulesForOrganizationIds(
    'appointment',
    organizationIds,
    (orgIds, apiOptions) => getActivatedVisitSchedulesForOrganizationIds(orgIds, studyConfig, apiOptions),
    apiOptions,
  );

const getCachedActivatedRequestsByOrgIds = async (
  orgId: OrgId[],
  studyConfig?: StudyConfig,
  apiOptions?: ApiOptions,
): Promise<VisitRequest[]> => {
  const getCachedVisitSchedules = await getCachedActivatedVisitSchedulesForOrganizationIds(
    orgId,
    studyConfig,
    apiOptions,
  );

  return getCachedVisitSchedules.getAllRequests();
};

export const VisitScheduleService = {
  getActiveRequestsByOrgId,
  getCachedActiveRequestsByOrgId,
  getCachedActivatedRequestsByOrgIds,
  getCachedActivatedVisitSchedulesForOrganizationIds,
};
